/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";

import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useLoading, { UseLoadingReturn } from "@/hooks/use-loading";
import { CancelRequestProps } from "@/types/requests.types";

export type CancelProps<T> = CancelRequestProps<T>;

export type UseCancelReturn<T> = {
  cancel: (props: CancelProps<T>) => Promise<any | void>;
  result: any | null;
} & UseLoadingReturn;

export type UseCancelProps<T> = {
  operation: (formData: T) => Promise<any | void>;
};

const useCancel = <T>({ operation }: UseCancelProps<T>): UseCancelReturn<T> => {
  const { isLoading, startLoading, endLoading, loadingIcon } = useLoading();
  const { showToast } = useToast();
  const [result, setResult] = useState<any | null>(null);

  const cancel = async ({
    formData,
    onSuccess,
    onError,
    successMessage = "",
    errorMessage = "",
  }: CancelProps<T>): Promise<any> => {
    startLoading();
    try {
      const operationResult = await operation(formData);
      setResult(operationResult);

      if (onSuccess) onSuccess();
      if (successMessage) showToast({ text: successMessage, type: "success" });

      return operationResult;
    } catch (e) {
      setResult(null);
      const message = buildErrorMessage(e, errorMessage);
      showToast({ text: message, type: "error" });
      if (onError) onError();
      throw e;
    } finally {
      endLoading();
    }
  };

  return {
    isLoading,
    startLoading,
    endLoading,
    loadingIcon,
    cancel,
    result,
  };
};

export default useCancel;
